import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, FileText, Info, Package, ReceiptText, Send, TrendingUp, Wallet } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { apiClient } from '../lib/api';
import { formatINR } from '../lib/gst';
import { BASIC_INTELLIGENCE_PLANS, buildInsights } from '../lib/insights';

function invoiceAmount(invoice) {
  return Number(invoice?.taxSummary?.grandTotal || 0);
}

function effectiveStatus(invoice) {
  if (invoice?.status === 'sent' && invoice?.dueDate) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(`${invoice.dueDate}T00:00:00`);
    if (!Number.isNaN(due.getTime()) && due < today) return 'overdue';
  }
  return invoice?.status || 'draft';
}

function isPastDue(date) {
  if (!date) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(`${date}T00:00:00`);
  return !Number.isNaN(due.getTime()) && due < today;
}

function StatCard({ icon: Icon, label, value, hint, tone = 'orange', children }) {
  const tones = {
    orange: 'text-orange-500 bg-orange-50',
    green: 'text-emerald-600 bg-emerald-50',
    amber: 'text-amber-600 bg-amber-50',
    slate: 'text-slate-600 bg-slate-100',
    red: 'text-red-600 bg-red-50',
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm min-h-[132px]">
      <div className="flex items-start justify-between gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tones[tone] || tones.orange}`}>
          <Icon className="w-5 h-5" />
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" className="text-slate-300 hover:text-slate-500" aria-label={`${label} details`}>
              <Info className="w-4 h-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs bg-slate-950 text-white">
            {hint}
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="text-xs uppercase tracking-wider text-slate-400 font-bold mt-4">{label}</div>
      <div className="font-heading text-2xl font-black text-slate-900 mt-1">{value}</div>
      {children}
    </div>
  );
}

export default function Dashboard() {
  const { user, plan, usage, refresh } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    invoices: 0,
    customers: 0,
    items: 0,
    invoiceValue: 0,
    sentCount: 0,
    overdueCount: 0,
    draftCount: 0,
    pastDueDraftCount: 0,
    pastDueDraftAmount: 0,
    receivable: 0,
    received: 0,
    overdue: 0,
    pending: 0,
  });
  const [insights, setInsights] = useState(() => buildInsights([], [], []));

  useEffect(() => {
    if (user === false) navigate('/login?redirect=/dashboard');
  }, [user, navigate]);

  useEffect(() => {
    if (user && user !== false) refresh();
  }, [user?.id, refresh]);

  const loadDashboard = useCallback(async () => {
    if (!user || user === false) return;
      const invoices = await apiClient.get('/invoices').catch(() => ({ data: [] }));
      const invoiceList = invoices.data || [];
      const [customers, items] = await Promise.allSettled([
        apiClient.get('/customers'),
        apiClient.get('/items'),
      ]);
      const customerList = customers.status === 'fulfilled' ? customers.value.data || [] : [];
      const itemList = items.status === 'fulfilled' ? items.value.data || [] : [];
      setInsights(buildInsights(invoiceList, customerList, itemList));
      const next = invoiceList.reduce((acc, inv) => {
        const status = effectiveStatus(inv);
        const amount = invoiceAmount(inv);
        acc.invoiceValue += amount;
        if (status === 'draft') {
          acc.draftCount += 1;
          if (isPastDue(inv.dueDate)) {
            acc.pastDueDraftCount += 1;
            acc.pastDueDraftAmount += amount;
          }
        }
        if (status === 'sent') {
          acc.sentCount += 1;
          acc.pending += amount;
        }
        if (status === 'overdue') {
          acc.overdueCount += 1;
          acc.overdue += amount;
        }
        if (status === 'sent' || status === 'overdue') acc.receivable += amount;
        if (status === 'paid') acc.received += amount;
        return acc;
      }, {
        invoices: invoiceList.length,
        customers: customerList.length,
        items: itemList.length,
        invoiceValue: 0,
        sentCount: 0,
        overdueCount: 0,
        draftCount: 0,
        pastDueDraftCount: 0,
        pastDueDraftAmount: 0,
        receivable: 0,
        received: 0,
        overdue: 0,
        pending: 0,
      });
      setStats({
        ...next,
        invoiceValue: Math.round(next.invoiceValue * 100) / 100,
        receivable: Math.round(next.receivable * 100) / 100,
        received: Math.round(next.received * 100) / 100,
        overdue: Math.round(next.overdue * 100) / 100,
        pending: Math.round(next.pending * 100) / 100,
        pastDueDraftAmount: Math.round(next.pastDueDraftAmount * 100) / 100,
      });
  }, [user]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    const handleFocus = () => loadDashboard();
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') loadDashboard();
    };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [loadDashboard]);

  if (!user || user === false) return null;

  const limit = usage?.limit;
  const usageLabel = limit == null
    ? <>{stats.invoices}/<span className="align-middle text-lg">∞</span></>
    : `${stats.invoices}/${limit}`;
  const invoiceLimitReached = limit != null && Number(usage?.invoices || 0) >= Number(limit);
  const attentionAmount = stats.overdue + stats.pastDueDraftAmount;
  const activePipeline = stats.received + stats.pending + attentionAmount;
  const receivedPct = activePipeline ? Math.round((stats.received / activePipeline) * 100) : 0;
  const attentionPct = activePipeline ? Math.round((attentionAmount / activePipeline) * 100) : 0;
  const pendingPct = activePipeline ? Math.max(0, 100 - receivedPct - attentionPct) : 0;
  const sentLabel = `${stats.sentCount + stats.overdueCount}`;
  const hasBasicIntelligence = BASIC_INTELLIGENCE_PLANS.has(plan);

  return (
    <TooltipProvider>
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="dashboard-page">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">{t('dashboard')}</h1>
            <p className="text-sm text-slate-500 mt-1">{t('welcomeLine')} {user.name || user.email}. {t('dashboardHistoryBasis')}</p>
          </div>
          {invoiceLimitReached ? (
            <Link to="/plan"><Button className="bg-orange-600 hover:bg-orange-700 text-white">{t('upgradePlan')}</Button></Link>
          ) : (
            <Link to="/create"><Button className="bg-slate-900 hover:bg-slate-800 text-white">{t('createInvoice')}</Button></Link>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={FileText}
            label={t('accountInvoices')}
            value={usageLabel}
            tone="slate"
            hint={t('accountInvoicesHint')}
          />
          <StatCard
            icon={Send}
            label={t('invoicesSent')}
            value={sentLabel}
            tone="amber"
            hint={t('invoicesSentHint')}
          >
            <div className="text-xs text-slate-500 mt-2">{stats.overdueCount} {t('overdue')}</div>
          </StatCard>
          <StatCard
            icon={Wallet}
            label={t('accountsReceivable')}
            value={formatINR(stats.receivable)}
            tone={stats.overdue ? 'red' : 'amber'}
            hint={t('accountsReceivableHint')}
          >
            <div className="text-xs text-slate-500 mt-2">{formatINR(stats.overdue)} {t('overdue')}</div>
          </StatCard>
          <StatCard
            icon={CheckCircle2}
            label={t('accountsReceived')}
            value={formatINR(stats.received)}
            tone="green"
            hint={t('accountsReceivedHint')}
          />
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <h2 className="font-heading text-xl font-black text-slate-900">{t('collectionSnapshot')}</h2>
                <p className="text-sm text-slate-500 mt-1">{t('collectionSnapshotCopy')}</p>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="text-slate-300 hover:text-slate-500" aria-label={`${t('collectionSnapshot')} ${t('details')}`}>
                    <Info className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs bg-slate-950 text-white">
                  {t('collectionSnapshotHint')}
                </TooltipContent>
              </Tooltip>
            </div>

            <div className="h-4 w-full rounded-full bg-slate-100 overflow-hidden flex">
              <div className="bg-emerald-500" style={{ width: `${receivedPct}%` }} />
              <div className="bg-amber-400" style={{ width: `${pendingPct}%` }} />
              <div className="bg-red-500" style={{ width: `${attentionPct}%` }} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
              <div className="rounded-lg border border-slate-200 p-4">
                <div className="text-xs uppercase tracking-wider font-bold text-slate-400">{t('collectionRate')}</div>
                <div className="font-heading text-2xl font-black text-slate-900 mt-1">{receivedPct}%</div>
              </div>
              <div className="rounded-lg border border-slate-200 p-4">
                <div className="text-xs uppercase tracking-wider font-bold text-slate-400">{t('invoiceValue')}</div>
                <div className="font-heading text-2xl font-black text-slate-900 mt-1">{formatINR(stats.invoiceValue)}</div>
              </div>
              <div className="rounded-lg border border-slate-200 p-4">
                <div className="text-xs uppercase tracking-wider font-bold text-slate-400">{t('drafts')}</div>
                <div className="font-heading text-2xl font-black text-slate-900 mt-1">{stats.draftCount}</div>
                {stats.pastDueDraftCount > 0 && (
                  <div className="text-xs text-red-600 font-semibold mt-1">{stats.pastDueDraftCount} {t('pastDue')} · {formatINR(stats.pastDueDraftAmount)}</div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mt-5 text-xs font-semibold text-slate-500">
              <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> {t('received')}</span>
              <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-amber-400" /> {t('pending')}</span>
              <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> {t('needsAttention')}</span>
            </div>
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="basic-intelligence-layer">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-orange-600">{t('intelligenceLayer')}</div>
              <h2 className="font-heading text-2xl font-black text-slate-900 mt-1">{hasBasicIntelligence ? t('moneyDashboard') : t('turnInsightsOn')}</h2>
              <p className="text-sm text-slate-500 mt-1">{hasBasicIntelligence ? t('basicLockedCopy') : t('basicPreviewCopy')}</p>
            </div>
            {!hasBasicIntelligence && (
              <Link to="/plan">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white">{t('upgradePlan')}</Button>
              </Link>
            )}
          </div>

          <div className={!hasBasicIntelligence ? 'pointer-events-none opacity-45' : ''}>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-3 mb-5">
              {[
                [t('billedThisMonth'), formatINR(insights.billedThisMonth)],
                [t('collectedThisMonth'), formatINR(insights.receivedThisMonth)],
                [t('outstanding'), formatINR(insights.receivable)],
                [t('overdue'), formatINR(insights.overdue)],
                [t('estimatedGst'), formatINR(insights.gstThisMonth)],
                [t('estimatedProfit'), formatINR(insights.grossProfit)],
              ].map(([label, value]) => (
                <div key={label} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="text-[10px] uppercase tracking-wider font-black text-slate-400">{label}</div>
                  <div className="font-heading text-xl font-black text-slate-900 mt-1">{value}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <section className="rounded-lg border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Wallet className="w-5 h-5 text-orange-500" />
                  <h3 className="font-heading font-black text-slate-900">{t('collections')}</h3>
                </div>
                <div className="space-y-3">
                  {Object.entries(insights.aging).map(([key, bucket]) => (
                    <div key={key}>
                      <div className="flex justify-between text-xs font-bold text-slate-500 mb-1">
                        <span>{bucket.label}</span>
                        <span>{formatINR(bucket.amount)} · {bucket.count}</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full bg-orange-500" style={{ width: `${insights.receivable ? Math.min(100, Math.round((bucket.amount / insights.receivable) * 100)) : 0}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="rounded-lg border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-4">
                  <ReceiptText className="w-5 h-5 text-orange-500" />
                  <h3 className="font-heading font-black text-slate-900">{t('gstView')}</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    [t('taxableAmount'), insights.gst.taxable],
                    ['CGST', insights.gst.cgst],
                    ['SGST', insights.gst.sgst],
                    ['IGST', insights.gst.igst],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-lg bg-slate-50 p-3">
                      <div className="text-[10px] uppercase tracking-wider font-black text-slate-400">{label}</div>
                      <div className="font-mono text-sm font-bold text-slate-900 mt-1">{formatINR(value)}</div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="rounded-lg border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-orange-500" />
                  <h3 className="font-heading font-black text-slate-900">{t('sales')}</h3>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="rounded-lg bg-slate-50 p-3">
                    <div className="text-[10px] uppercase tracking-wider font-black text-slate-400">{t('averageInvoiceValue')}</div>
                    <div className="font-heading text-lg font-black text-slate-900">{formatINR(insights.averageInvoiceValue)}</div>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-3">
                    <div className="text-[10px] uppercase tracking-wider font-black text-slate-400">{t('invoices')}</div>
                    <div className="font-heading text-lg font-black text-slate-900">{insights.invoices}</div>
                  </div>
                </div>
                <div className="space-y-2">
                  {insights.topCustomers.slice(0, 3).map((row) => (
                    <div key={row.name} className="flex justify-between text-sm">
                      <span className="font-bold text-slate-700 truncate">{row.name}</span>
                      <span className="font-mono text-slate-500">{formatINR(row.billed)}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section className="rounded-lg border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Package className="w-5 h-5 text-orange-500" />
                  <h3 className="font-heading font-black text-slate-900">{t('profit')}</h3>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="rounded-lg bg-slate-50 p-3">
                    <div className="text-[10px] uppercase tracking-wider font-black text-slate-400">{t('grossMargin')}</div>
                    <div className="font-heading text-lg font-black text-slate-900">{insights.grossMargin}%</div>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-3">
                    <div className="text-[10px] uppercase tracking-wider font-black text-slate-400">{t('estimatedProfit')}</div>
                    <div className="font-heading text-lg font-black text-slate-900">{formatINR(insights.grossProfit)}</div>
                  </div>
                </div>
                <div className="space-y-2">
                  {insights.topItems.slice(0, 3).map((row) => (
                    <div key={row.description} className="flex justify-between text-sm">
                      <span className="font-bold text-slate-700 truncate">{row.description}</span>
                      <span className="font-mono text-slate-500">{formatINR(row.profit)}</span>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
